import { DEFAULT_DEPARTMENTS_RESPONSE } from "../pages/LandingPage/constants";
import {
  UserPermission,
  UserData,
  IAMUserState,
  OktaSSOSession,
  CdcmResponse,
} from "../types";
import {
  getAPIBffBase,
  getDetailsApiUrl,
  getMeApiUrl,
  getOktaSessionUrl,
  getOktaPreviewSessionUrl,
  getPurchaseOrderDetailsApiUrl,
} from "../utils";

const cdcmApiUrl = getAPIBffBase();
const meApiUrl = getMeApiUrl();
const detailsApiUrl = getDetailsApiUrl();
const purchaseOrderDetailsApiUrl = getPurchaseOrderDetailsApiUrl();
const oktaPreviewSessionUrl = getOktaPreviewSessionUrl();
const oktaSessionUrl = getOktaSessionUrl();

export async function getUserIdentityService(): Promise<UserData> {
  try {
    const response = await fetch(`${meApiUrl}rest/v1.2/users/me`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const data = await response.json();

    if (data.fullUser) {
      return data.fullUser;
    }
    throw new Error();
  } catch (error) {
    console.error("Unable to get user info from identity service", error);
    throw new Error("Network error: Unable to fetch user identity.");
  }
}

export async function getUserIdentityServiceFromDetailsApi(): Promise<IAMUserState> {
  try {
    const response = await fetch(`${detailsApiUrl}user/details`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const data = (await response.json()) as IAMUserState;

    if (data.iamUser && data.extendedAccountInfo) {
      return data;
    }
    throw new Error();
  } catch (error) {
    console.error("Unable to get user info from details API", error);
    throw new Error("Network error: Unable to fetch IAM user identity.");
  }
}

export async function getUserIdentityServiceFromCdcmApi(
  enterpriseId: string,
  storeId: string,
  integrationApp: string,
): Promise<IAMUserState> {
  try {
    const response = await fetch(`${cdcmApiUrl}auth/user-info`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-enterprise-id": `${enterpriseId}`,
        "x-store-id": `${storeId}`,
        "integration-app": `${integrationApp}`,
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    const result = (await response.json()) as CdcmResponse;
    const data = result.resultObj as IAMUserState;

    if (data.extendedAccountInfo && data.eisGroups) {
      return data;
    }
    throw new Error();
  } catch (error) {
    console.error("Unable to get user info from CDCM identity service", error);
    throw new Error(
      "Network error: Unable to fetch user identity from CDCM API.",
    );
  }
}

export async function getPurchaseOrderDetailsApi(): Promise<IAMUserState> {
  try {
    const response = await fetch(
      `${purchaseOrderDetailsApiUrl}purchase-order-bff/user-details-with-account-type/A`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      },
    );
    const data: IAMUserState = await response.json();

    if (data.iamUser && data.iamUser.authorities) {
      return data;
    }
    throw new Error();
  } catch (error) {
    console.error("Unable to get user info from identity service", error);
    throw new Error("Network error: Unable to fetch IAM user identity.");
  }
}

export async function getDepartmentsByRoles(
  roleName: string,
): Promise<CdcmResponse> {
  try {
    const url = `${cdcmApiUrl}auth/departments/role`;
    const getUsersRequest = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
        roleName,
      },
    };
    return await fetch(url, getUsersRequest).then((res) => res.json());
  } catch (error) {
    console.error("Unable to get user departments", error);
    return DEFAULT_DEPARTMENTS_RESPONSE;
  }
}

export async function getPermissions(
  loginId: string,
): Promise<Array<UserPermission>> {
  try {
    const response = await fetch(`${cdcmApiUrl}auth/permissions/${loginId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });
    return await response.json();
  } catch (error) {
    console.error("Unable to get user permissions", error);
    return [];
  }
}

export async function getOktaPreviewSession(): Promise<OktaSSOSession> {
  return fetch(`${oktaPreviewSessionUrl}api/v1/sessions/me`, {
    method: "GET",
    credentials: "include",
  })
    .then((res) => res.json())
    .catch((error) => {
      console.error(error);
      return {};
    });
}

export async function getOktaSession(): Promise<OktaSSOSession> {
  return fetch(`${oktaSessionUrl}api/v1/sessions/me`, {
    method: "GET",
    credentials: "include",
  })
    .then((res) => res.json())
    .catch((error) => {
      console.error(error);
      return {};
    });
}

export function getLoginId(): string {
  const storage = localStorage.getItem("okta-token-storage") as string;
  const json = JSON.parse(storage);
  return json?.accessToken?.claims?.cdksid as string;
}

export function getAccessToken(): string {
  const storage = localStorage.getItem("okta-token-storage") as string;
  const json = JSON.parse(storage);
  return json?.accessToken?.accessToken as string;
}
